import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import { useGetProfileQuery } from "../../../store/api/authApi";
import type { MaintenanceRequest } from "../../../store/api/maintenanceApi";
import {
	useAssignMaintenanceRequestMutation,
	useCompleteMaintenanceRequestMutation,
	useGetMaintenanceRequestQuery,
} from "../../../store/api/maintenanceApi";
import { useListUsersQuery } from "../../../store/api/usersApi";

interface MaintenanceDetailModalProps {
	request: MaintenanceRequest | null;
	isOpen: boolean;
	onClose: () => void;
	onUpdate: () => void;
	onEdit?: (request: MaintenanceRequest) => void;
}

const MaintenanceDetailModal: React.FC<MaintenanceDetailModalProps> = ({
	request,
	isOpen,
	onClose,
	onUpdate,
	onEdit,
}) => {
	const { data: userProfile } = useGetProfileQuery();
	const isAdmin = userProfile?.role === "admin";
	const { data: requestData, isLoading } = useGetMaintenanceRequestQuery(
		request?.id || "",
		{
			skip: !request?.id || !isOpen,
		},
	);

	const { data: usersData } = useListUsersQuery(
		{ role: "technician", page_size: 100 },
		{ skip: !isAdmin },
	);

	const [assignRequest, { isLoading: isAssigning }] =
		useAssignMaintenanceRequestMutation();
	const [completeRequest, { isLoading: isCompleting }] =
		useCompleteMaintenanceRequestMutation();

	const [showAssignForm, setShowAssignForm] = useState(false);
	const [showCompleteForm, setShowCompleteForm] = useState(false);
	const [selectedTechnician, setSelectedTechnician] = useState("");
	const [resolutionNotes, setResolutionNotes] = useState("");
	const [actualCost, setActualCost] = useState("");

	const currentRequest = requestData || request;

	if (!isOpen || !currentRequest) return null;

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-success/10 text-success";
			case "in_progress":
				return "bg-primary/10 text-primary";
			case "pending":
				return "bg-warning/10 text-warning";
			case "cancelled":
				return "bg-muted text-muted-foreground";
			case "on_hold":
				return "bg-muted/50 text-muted-foreground";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	const getPriorityColor = (priority: string) => {
		switch (priority) {
			case "urgent":
				return "bg-error/10 text-error";
			case "high":
				return "bg-warning/10 text-warning";
			case "medium":
				return "bg-primary/10 text-primary";
			case "low":
				return "bg-muted text-muted-foreground";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	const handleAssign = async () => {
		if (!currentRequest || !selectedTechnician) return;

		try {
			await assignRequest({
				id: currentRequest.id,
				data: { technician_id: selectedTechnician },
			}).unwrap();

			setShowAssignForm(false);
			setSelectedTechnician("");
			onUpdate();
		} catch (error: any) {
			console.error("Assign error:", error);
			alert(
				error?.data?.error ||
					"Failed to assign maintenance request. Please try again.",
			);
		}
	};

	const handleComplete = async () => {
		if (!currentRequest) return;

		try {
			await completeRequest({
				id: currentRequest.id,
				data: {
					resolution_notes: resolutionNotes || undefined,
					actual_cost: actualCost ? parseInt(actualCost) : undefined,
				},
			}).unwrap();

			setShowCompleteForm(false);
			setResolutionNotes("");
			setActualCost("");
			onUpdate();
		} catch (error: any) {
			console.error("Complete error:", error);
			alert(
				error?.data?.error ||
					"Failed to complete maintenance request. Please try again.",
			);
		}
	};

	const technicianOptions: SelectOption[] =
		usersData?.results
			.filter((user) => user.role === "technician")
			.map((tech) => ({
				value: tech.id,
				label: `${tech.first_name} ${tech.last_name}`,
			})) || [];

	return (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 z-[9998] animate-fade-in"
				onClick={onClose}
			/>

			{/* Modal */}
			<div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4 pointer-events-none">
				<div className="bg-card rounded-lg sm:rounded-lg border border-border w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[90vh] overflow-hidden animate-scale-in pointer-events-auto flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between p-4 sm:p-6 border-b border-border flex-shrink-0">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Maintenance Request Details
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Request ID: {currentRequest.id}
							</p>
						</div>
						<Button variant="ghost" size="icon" onClick={onClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>

					{/* Content */}
					<div className="p-4 sm:p-6 overflow-y-auto flex-1">
						{isLoading ? (
							<div className="flex items-center justify-center py-12">
								<Icon
									name="Loader2"
									size={32}
									className="animate-spin text-primary"
								/>
							</div>
						) : (
							<div className="space-y-6">
								{/* Status and Priority Badges */}
								<div className="flex items-center justify-between">
									<span
										className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(
											currentRequest.status,
										)}`}
									>
										{currentRequest.status.replace("_", " ")}
									</span>
									<span
										className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getPriorityColor(
											currentRequest.priority,
										)}`}
									>
										{currentRequest.priority} Priority
									</span>
								</div>

								{/* Request Information */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Title
										</label>
										<p className="text-lg text-foreground mt-1">
											{currentRequest.title}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Property
										</label>
										<p className="text-lg text-foreground mt-1">
											{currentRequest.property_name || "N/A"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Requested By
										</label>
										<p className="text-lg text-foreground mt-1">
											{currentRequest.requested_by_name || "N/A"}
										</p>
									</div>

									{currentRequest.assigned_to_name && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Assigned To
											</label>
											<p className="text-lg text-foreground mt-1">
												{currentRequest.assigned_to_name}
											</p>
										</div>
									)}

									{currentRequest.location && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Location
											</label>
											<p className="text-lg text-foreground mt-1">
												📍 {currentRequest.location}
											</p>
										</div>
									)}

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Requested Date
										</label>
										<p className="text-lg text-foreground mt-1">
											{formatDate(currentRequest.requested_date)}
										</p>
									</div>

									{currentRequest.scheduled_date && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Scheduled Date
											</label>
											<p className="text-lg text-foreground mt-1">
												{formatDate(currentRequest.scheduled_date)}
											</p>
										</div>
									)}

									{currentRequest.completed_date && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Completed Date
											</label>
											<p className="text-lg text-foreground mt-1">
												{formatDate(currentRequest.completed_date)}
											</p>
										</div>
									)}

									{currentRequest.estimated_cost && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Estimated Cost
											</label>
											<p className="text-lg font-semibold text-foreground mt-1">
												{formatCurrency(currentRequest.estimated_cost)}
											</p>
										</div>
									)}

									{currentRequest.actual_cost && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Actual Cost
											</label>
											<p className="text-lg font-semibold text-foreground mt-1">
												{formatCurrency(currentRequest.actual_cost)}
											</p>
										</div>
									)}
								</div>

								{/* Description */}
								<div>
									<label className="text-sm font-medium text-muted-foreground mb-2 block">
										Description
									</label>
									<p className="text-foreground whitespace-pre-wrap">
										{currentRequest.description}
									</p>
								</div>

								{/* Resolution Notes */}
								{currentRequest.resolution_notes && (
									<div>
										<label className="text-sm font-medium text-muted-foreground mb-2 block">
											Resolution Notes
										</label>
										<p className="text-foreground whitespace-pre-wrap">
											{currentRequest.resolution_notes}
										</p>
									</div>
								)}

								{/* Notes */}
								{currentRequest.notes && (
									<div>
										<label className="text-sm font-medium text-muted-foreground mb-2 block">
											Additional Notes
										</label>
										<p className="text-foreground whitespace-pre-wrap">
											{currentRequest.notes}
										</p>
									</div>
								)}

								{/* Admin Actions */}
								{isAdmin && (
									<div className="border-t border-border pt-4 space-y-4">
										{/* Assign Form */}
										{!currentRequest.assigned_to_id &&
											currentRequest.status !== "completed" &&
											!showAssignForm && (
												<Button
													onClick={() => setShowAssignForm(true)}
													className="w-full"
												>
													<Icon name="UserPlus" size={16} className="mr-2" />
													Assign to Technician
												</Button>
											)}

										{showAssignForm && (
											<div className="space-y-4 p-4 bg-muted/30 rounded-lg">
												<h3 className="text-sm font-medium text-foreground">
													Assign to Technician
												</h3>
												<Select
													options={technicianOptions}
													value={selectedTechnician}
													onValueChange={(value) =>
														setSelectedTechnician(value as string)
													}
													placeholder="Select technician"
												/>
												<div className="flex items-center space-x-3">
													<Button
														onClick={handleAssign}
														disabled={isAssigning || !selectedTechnician}
														className="flex-1"
													>
														{isAssigning ? (
															<>
																<Icon
																	name="Loader2"
																	size={16}
																	className="mr-2 animate-spin"
																/>
																Assigning...
															</>
														) : (
															<>
																<Icon name="Check" size={16} className="mr-2" />
																Assign
															</>
														)}
													</Button>
													<Button
														variant="outline"
														onClick={() => {
															setShowAssignForm(false);
															setSelectedTechnician("");
														}}
														disabled={isAssigning}
													>
														Cancel
													</Button>
												</div>
											</div>
										)}

										{/* Complete Form */}
										{currentRequest.status !== "completed" &&
											currentRequest.assigned_to_id &&
											!showCompleteForm && (
												<Button
													onClick={() => setShowCompleteForm(true)}
													className="w-full"
												>
													<Icon name="CheckCircle" size={16} className="mr-2" />
													Mark as Completed
												</Button>
											)}

										{showCompleteForm && (
											<div className="space-y-4 p-4 bg-muted/30 rounded-lg">
												<h3 className="text-sm font-medium text-foreground">
													Complete Maintenance Request
												</h3>
												<Input
													label="Actual Cost (₦)"
													type="number"
													placeholder="0"
													value={actualCost}
													onChange={(e) => setActualCost(e.target.value)}
													min="0"
												/>
												<div>
													<label className="block text-sm font-medium text-foreground mb-2">
														Resolution Notes
													</label>
													<textarea
														rows={3}
														placeholder="Describe how the issue was resolved..."
														value={resolutionNotes}
														onChange={(e) => setResolutionNotes(e.target.value)}
														className="w-full px-3 py-2 border border-border rounded-lg text-foreground bg-background placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
													/>
												</div>
												<div className="flex items-center space-x-3">
													<Button
														onClick={handleComplete}
														disabled={isCompleting}
														className="flex-1"
													>
														{isCompleting ? (
															<>
																<Icon
																	name="Loader2"
																	size={16}
																	className="mr-2 animate-spin"
																/>
																Completing...
															</>
														) : (
															<>
																<Icon
																	name="CheckCircle"
																	size={16}
																	className="mr-2"
																/>
																Complete
															</>
														)}
													</Button>
													<Button
														variant="outline"
														onClick={() => {
															setShowCompleteForm(false);
															setResolutionNotes("");
															setActualCost("");
														}}
														disabled={isCompleting}
													>
														Cancel
													</Button>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						)}
					</div>

					{/* Footer */}
					<div className="p-4 sm:p-6 border-t border-border bg-muted/20 flex-shrink-0">
						<div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
							{isAdmin && onEdit && (
								<Button
									variant="outline"
									onClick={() => {
										onEdit(currentRequest);
										onClose();
									}}
									iconName="Edit"
									iconPosition="left"
									className="w-full sm:w-auto"
									size="sm"
								>
									Edit Request
								</Button>
							)}
							<Button
								variant="outline"
								onClick={onClose}
								className="w-full sm:w-auto sm:ml-auto"
								size="sm"
							>
								Close
							</Button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default MaintenanceDetailModal;
